import React from "react";
import { motion } from "framer-motion";
import { Rocket, Cog, Home, Users, Brain, Monitor } from "lucide-react";
import { FAQItem } from "../components/faqitem";
import { PerkItem } from "../components/perkitem";
import { PageHeader } from "../components/pageheader";
import SectionTitle from "../components/sectiontitle";
import CTAButton from "../components/button";

/* ---------- Container ---------- */
const Container: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
  className = "",
  children,
}) => (
  <div className={`mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

/* ---------- Types & Data ---------- */
type Role = {
  title: string;
  department: string;
  type: string;
  location: string;
  summary: string;
  tags?: string[];
  applyEmail?: string;
};

/* ---------- Open positions ---------- */

const OPEN_ROLES: Role[] = [
  {
    title: "Experienced Platform Engineer",
    department: "Platforms",
    type: "Full-time",
    location: "Hybrid Malmö",
    summary:
      "Join our Platforms team, building developer platforms with Backstage for well-known larger Nordic companies. You're a full-stack-minded Platform engineer comfortable across platform capabilities like cloud-native services, infrastructure and security — turning fragmented tooling into intuitive self-services going the extra mile for a great developer experience, helping teams ship fast and with confidence.",
    tags: [
      "Platform Engineering",
      "Backstage",
      "Full-stack",
      "Kubernetes",
      "Cloud",
      "Infrastructure",
    ],
    applyEmail: "jobs@bold-n-brave.com",
  },
  {
    title: "Senior .NET Backend Engineer",
    department: "Backend",
    type: "Contract",
    location: "Hybrid Malmö",
    summary:
      "Join a cross-functional product team working in a modern microservices architecture using .NET 9, C#, Kubernetes and event-driven patterns. You’ll work on large-scale distributed systems, including payment solutions, with a strong focus on reliability, collaboration and continuous improvement.",
    tags: [
      ".NET",
      "C#",
      "Kubernetes",
      "Microservices",
      "Payments",
      "Event-Driven Architecture",
    ],
    applyEmail: "jobs@bold-n-brave.com",
  },
];

/* ---------- Animations ---------- */
const workListVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
};

const Badge: React.FC<React.PropsWithChildren> = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-white/15 px-2.5 py-1 text-xs text-white/70">
    {children}
  </span>
);

const RoleRow: React.FC<{ r: Role }> = ({ r }) => {
  const applyHref = `mailto:${
    r.applyEmail ?? "info@bold-n-brave.com"
  }?subject=${encodeURIComponent("Application: " + r.title)}`;

  return (
    <li className="group border-t border-white/10 py-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
        {/* Vänsterdel: titel, beskrivning och badges */}
        <div className="sm:max-w-[70%]">
          <h3 className="text-base sm:text-lg font-semibold">{r.title}</h3>
          <p className="mt-1 text-sm text-white/70 leading-relaxed">
            {r.summary}
          </p>

          {/* Badges under beskrivningen */}
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge>{r.type}</Badge>
            <Badge>{r.location}</Badge>
            <Badge>{r.department}</Badge>
            {r.tags?.map((t) => (
              <Badge key={t}>{t}</Badge>
            ))}
          </div>
        </div>

        {/* Högerdel: Apply-knapp */}
        <div className="sm:flex sm:items-start sm:justify-end sm:flex-shrink-0">
          <CTAButton href={applyHref} variant="small">
            Apply
          </CTAButton>
        </div>
      </div>
    </li>
  );
};
/* ---------- Main Component ---------- */
export default function JobsPage() {
  return (
    <section className="py-14">
      <Container>
        <PageHeader
          eyebrow="Join us"
          title="A senior team that ships"
          text="We’re a compact core team with a strong platform crew and a network of specialists. If you love pragmatic problem-solving and polished delivery, you’ll fit right in."
        />

        {/* ---------- Open positions ---------- */}
        <section className="my-24">
          <SectionTitle title="We currently look for" />

          {OPEN_ROLES.length > 0 ? (
            <motion.ul
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              className="divide-y divide-white/10"
            >
              {OPEN_ROLES.map((r) => (
                <RoleRow key={r.title} r={r} />
              ))}
            </motion.ul>
          ) : (
            <div className="rounded-xl border border-white/10 p-6 bg-[#0f1a18]">
              <h3 className="font-semibold">No openings right now</h3>
              <p className="mt-2 text-white/70">
                We’re not hiring for specific roles at the moment. But we’re
                always happy to hear from senior people who care about quality
                and delivery.
              </p>
              <CTAButton
                href={
                  "mailto:info@bold-n-brave.com?subject=Speculative%20application"
                }
                variant="small"
              >
                Send a speculative application
              </CTAButton>
            </div>
          )}
        </section>

        {/* ---------- Benefits / Perks ---------- */}
        <section className="mb-24">
          <SectionTitle title="Benefits with BoldBrave" center />

          <motion.div
            className="mt-10 grid gap-6 sm:grid-cols-3"
            variants={workListVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
          >
            <PerkItem
              icon={<Home />}
              title="Work where you thrive"
              text="Our base is in Malmö, but we collaborate from wherever makes sense — as long as the Wi-Fi holds up and the coffee’s decent."
            />

            <PerkItem
              icon={<Monitor />}
              title="Tools that don’t fight you"
              text="We make sure you’ve got proper gear — from laptop and screens to the small things that make craft and flow effortless."
            />

            <PerkItem
              icon={<Cog />}
              title="No process theatre"
              text="We skip the unnecessary rituals. Just smart routines, strong ownership, and people who get it done without the drama."
            />

            <PerkItem
              icon={<Brain />}
              title="Curious minds welcome"
              text="We believe in senior curiosity — the drive to learn new things, share insights, and keep improving how we build."
            />

            <PerkItem
              icon={<Rocket />}
              title="Real impact"
              text="We build products and platforms that actually ship — used by real people, solving real problems for real companies."
            />

            <PerkItem
              icon={<Users />}
              title="Good people only"
              text="No egos, no nonsense. Just kind, competent people who enjoy working together and take pride in their craft."
            />
          </motion.div>
        </section>

        {/* ---------- Speculative applications ---------- */}
        <section className="mb-24 text-center">
          <SectionTitle title="Not seeing your role?" center />
          <p className="max-w-2xl mx-auto text-white/70 mb-8">
            We collaborate with great people. If you don’t see an exact match
            but think you could help us ship better and faster, tell us how.
          </p>
          <CTAButton href="/hire">Get in touch</CTAButton>
        </section>

        {/* ---------- FAQ ---------- */}
        <section id="faq" className="py-16">
          <Container>
            <SectionTitle center title="Frequently Asked Questions" />
            <div className="space-y-3">
              <FAQItem
                q="So... are you a consultancy or a freelance network?"
                a={
                  <p>
                    Somewhere in between. We run as a small, independent agency
                    — but the team is made up of senior people who know their
                    craft, some employed, some running their own companies. Same
                    standards, same drive. No middlemen.
                  </p>
                }
              />

              <FAQItem
                q="How does it work if I have my own company?"
                a={
                  <p>
                    Easy. You invoice us, we handle the client. We set the rate
                    together, keep it transparent, and pay on time. You get to
                    focus on doing the work, not chasing admin.
                  </p>
                }
              />

              <FAQItem
                q="What kind of projects do you usually take on?"
                a={
                  <p>
                    Mostly complex stuff that actually matters — digital
                    products, large-scale e-commerce, platform builds, and
                    design systems. Work that needs both brains and experience,
                    not just headcount.
                  </p>
                }
              />

              <FAQItem
                q="What tech do you use?"
                a={
                  <p>
                    Depends on the client, but we tend to hang around
                    TypeScript, React, Node, .NET, cloud stuff, and modern
                    headless stacks. We’re allergic to hype-driven architecture
                    and love clean, practical solutions.
                  </p>
                }
              />

              <FAQItem
                q="Is everything remote?"
                a={
                  <p>
                    Malmö is home base, and we meet up there now and then for
                    planning or workshops. Some clients like hybrid setups, but
                    we keep things flexible and sane.
                  </p>
                }
              />

              <FAQItem
                q="How big are the teams?"
                a={
                  <p>
                    Usually small and senior. A handful of people who know what
                    they’re doing. We’d rather have three great builders than a
                    football team of “resources”.
                  </p>
                }
              />

              <FAQItem
                q="What kind of people fit in at BoldBrave?"
                a={
                  <p>
                    People who take pride in what they build, care about
                    quality, and don’t need to be micromanaged. Low ego, high
                    curiosity. If that sounds like you, you’ll probably fit
                    right in.
                  </p>
                }
              />

              <FAQItem
                q="How do I join?"
                a={
                  <p>
                    Drop us a line and tell us a bit about what you do, what
                    you’ve built, and what kind of work you like. If there’s a
                    match, we’ll grab a coffee (real or virtual) and take it
                    from there.
                  </p>
                }
              />

              <FAQItem
                q="What’s this membership thing I’ve heard about?"
                a={
                  <p>
                    It’s simple: being part of BoldBrave means access to great
                    projects, smart people, and shared opportunities — without
                    the corporate overhead. Whether you’re on our payroll or
                    have your own AB doesn’t matter.
                  </p>
                }
              />
            </div>
          </Container>
        </section>
      </Container>
    </section>
  );
}
